import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DateTime } from '@syncfusion/ej2-angular-charts';
import { CloseEventArgs, DialogComponent } from '@syncfusion/ej2-angular-popups';
import { Observable, Subject, Subscription, catchError, finalize, tap } from 'rxjs';
import { PessoaDoencaAtivacaoDto, PessoaDoencaConsultaDto } from 'src/app/dtos/doencas/pessoa-doenca-dto';
import { SelectDataSource } from 'src/app/libs/ax-select/select-data-source';
import { ToastService } from 'src/app/libs/toast/toast.service';
import { Cid10Service } from 'src/app/servicos/cid10.service';
import { PessoaDoencaService } from 'src/app/servicos/pessoa-doenca.service';
import { AtendimentoAtualConfiguracao } from '../../atendimento-medico/atendimento-cadastro/atendimento-cadastro.component';
import { ConfirmService } from 'src/app/libs/confirm/confirm.service';
import { TranslateService } from '@ngx-translate/core';
import { ALERTA, DOENCA, ERRO } from 'src/app/i18n/constantes';

interface ReativarDoencaForm {
  id: FormControl<number>,
  atendimentoMedicoId: FormControl<number | null>,
  dataAtendimento: FormControl<Date | null>,
  cid10Codigo: FormControl<string | null>,
  descricao: FormControl<string | null>
}

@Component({
  selector: 'app-doenca-reativacao',
  templateUrl: './doenca-reativacao.component.html',
  styleUrls: ['./doenca-reativacao.component.less']
})
export class DoencaReativacaoComponent implements OnInit {
  modalOk: boolean = false;
  
  @Input()
  atendimentoAtualConfiguracao: AtendimentoAtualConfiguracao | null = null;
  
  @ViewChild('modal')
  modal!: DialogComponent;

  submetido: boolean = false;
  emOperacao: boolean = false;
  temAlteracao: boolean = false;

  subsModelo: Subscription | null = null;
  
  ultimaDescricao: string | null = null;
  
  modelo: FormGroup<ReativarDoencaForm> = this.fb.group<ReativarDoencaForm>({
    id: this.fb.nonNullable.control<number>(0, [Validators.required]),
    dataAtendimento: this.fb.control<Date | null>(null),
    cid10Codigo: this.fb.nonNullable.control<string | null>('', Validators.required),
    descricao: this.fb.nonNullable.control<string | null>(null),
    atendimentoMedicoId: this.fb.nonNullable.control<number | null>(null),
  });

  dsCids10: SelectDataSource = this.cid10Servico.dsCids10();

  assunto: Subject<boolean> = new Subject<boolean>();

  constructor(private fb: FormBuilder, 
    private cid10Servico: Cid10Service, 
    private toast: ToastService,
    private confirm: ConfirmService,
    private translate: TranslateService,
    private pessoaDoencaServico: PessoaDoencaService) { }

  ngOnInit(): void {
    this.subsModelo = this.modelo.valueChanges.subscribe(() =>  this.temAlteracao = true);
  }

  ngOnDestroy(): void {
    this.subsModelo?.unsubscribe();
  }

  reativar() {
    this.submetido = true;

    if(this.emOperacao)
      return;

    if(!this.modelo.valid)
      return;

    let doenca: PessoaDoencaAtivacaoDto = {
      id: this.modelo.controls.id.value,
      dataAtendimento: this.modelo.controls.dataAtendimento.value,
      descricao: this.modelo.controls.descricao.value,
      atendimentoMedicoId: this.atendimentoAtualConfiguracao?.id ?? null
    };

    this.emOperacao = true;
    this.pessoaDoencaServico.reativarDoenca(doenca)
    .pipe(
      catchError(err => {
        this.toast.showWarning(this.translate.instant(DOENCA.ERRO.REATIVACAO));
        throw err;
      }),
      finalize(() => {
        this.emOperacao = false;
        this.modal.hide();
      })
    )
    .subscribe(() => {
      this.temAlteracao = false;
      this.emOperacao = false;
      this.toast.showSuccess(this.translate.instant(DOENCA.SUCESSO.REATIVACAO));
      this.modal.hide();
    });
  }

  fechandoModal(args: CloseEventArgs) {
    if(this.emOperacao) {
      args.cancel = true;
      return;
    }

    if(this.temAlteracao) {
      args.cancel = true;
      this.confirmarFechamento();
      return;
    }

    this.assunto.next(true);
  }

  abrir(id: number): Observable<boolean> {
    this.submetido = false;
    
    this.modal.show(true);

    this.carregar(id);

    return new Observable((subscriber) => {
      let subs = this.assunto.subscribe((val) => {
        subs.unsubscribe();
        subscriber.next(val);
        subscriber.complete();
      });

      return { unsubscribe: () => subs.unsubscribe() }
    });
  }
  
  carregar(id: number) {
    this.pessoaDoencaServico.consultarDoenca(id)
    .pipe(
      catchError((err) => {
        this.toast.showError(this.translate.instant(ERRO.CARREGAMENTO));
        this.modal.hide();
        throw err;
      }),
      tap((dto: PessoaDoencaConsultaDto) => {
        this.ultimaDescricao = dto.descricao;
        this.modelo.controls.cid10Codigo.setValue(dto.cid10Id, { emitEvent: false });
        this.modelo.controls.descricao.setValue('', { emitEvent: false });
        this.modelo.controls.dataAtendimento.setValue(this.atendimentoAtualConfiguracao?.data ?? null, { emitEvent: false });
        this.modelo.controls.atendimentoMedicoId.setValue(this.atendimentoAtualConfiguracao?.id ?? null, { emitEvent: false });
        this.modelo.controls.id.setValue(dto.id, { emitEvent: false });
      })
    )
    .subscribe();
  }

  confirmarFechamento() {
    this.confirm
    .confirm(this.translate.instant(ALERTA.OPERACAO_PENDENTE), { height: '220px' })
    .subscribe(val => {
      if(val) {
        this.cancelar();
      }
    })
  }

  cancelar() {
    this.temAlteracao = false;
    this.modal.hide();
  }
}
